import { toast } from "sonner";
import { Radar, Volume2, VolumeX, Bell, BellOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Banner } from "@/components/common";
import { usePoll } from "@/hooks/usePoll";
import { api, errMsg } from "@/lib/api";
import { SignalBanner, KpiStrip } from "@/components/oil/OilTop";
import { SpreadChart } from "@/components/oil/SpreadChart";
import { Calculator } from "@/components/oil/Calculator";
import { StrategyConfig, GateConfig } from "@/components/oil/OilConfig";
import { OpenTrade, OilLive, SymbolLock } from "@/components/oil/OilLive";
import { OilHistory, Replay, Maintenance } from "@/components/oil/OilHistory";
import { OilDemo } from "@/components/oil/OilDemo";

export default function OilPairs() {
  const { data: s, reload } = usePoll("/oil/state", 10000);
  const setCfg = async (body) => {
    try { await api.put("/oil/config", body); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const scan = async () => {
    try { const { data } = await api.post("/oil/scan"); toast.success(data.spread != null ? `Spread ${data.spread}` : "Scanned, no prices yet"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!s) return <div className="text-sm text-slate-500" data-testid="oil-loading">Loading Oil Pairs</div>;
  const c = s.config;
  const toggleNotif = () => {
    if (!c.browser_notifications && "Notification" in window) Notification.requestPermission();
    setCfg({ browser_notifications: !c.browser_notifications });
  };
  return (
    <div className="space-y-5" data-testid="oil-pairs-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-100">Bollinger Oil Pairs</h1>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 rounded-md border border-slate-800 px-3 py-1.5 text-sm text-slate-300"><Switch data-testid="oil-engine-toggle" checked={c.enabled} onCheckedChange={(v) => setCfg({ enabled: v })} />Engine {c.enabled ? "ON" : "OFF"}</label>
          <div className="flex overflow-hidden rounded-md border border-slate-800" data-testid="oil-mode-toggle">
            {["alert", "auto"].map((m) => (
              <button key={m} data-testid={`oil-mode-${m}`} onClick={() => setCfg({ mode: m })} className={`px-3 py-1.5 text-sm capitalize transition-colors ${c.mode === m ? "bg-indigo-500 text-white" : "text-slate-400 hover:text-slate-100"}`}>{m}</button>
            ))}
          </div>
          <Button data-testid="oil-audio-toggle" variant="outline" size="sm" className="border-slate-700 bg-transparent" onClick={() => setCfg({ audio_alerts: !c.audio_alerts })}>{c.audio_alerts ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}</Button>
          <Button data-testid="oil-notifications-toggle" variant="outline" size="sm" className="border-slate-700 bg-transparent" onClick={toggleNotif}>{c.browser_notifications ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}</Button>
          <Button data-testid="oil-scan-button" size="sm" className="btn-gold" onClick={scan}><Radar className="mr-1.5 h-4 w-4" />Scan now</Button>
        </div>
      </div>
      {s.feed?.source === "public" && <Banner tone="info" testid="oil-public-feed-banner">Showing the public feed: delayed Brent ({s.feed.public_symbols.brent}) and WTI ({s.feed.public_symbols.wti}) futures prices{s.feed.last_price_at ? `, last price ${new Date(s.feed.last_price_at).toLocaleTimeString("en-GB", { timeZone: "Europe/London", hour: "2-digit", minute: "2-digit" })} London` : ""}. Good for viewing and paper signals; live orders only go out once MT5 is linked and its prices take over.</Banner>}
      {s.broker.configured && !(s.symbols.brent && s.symbols.wti) && <Banner tone="warn">Brent and WTI symbols are not resolved yet. Pin them in the symbol lock card if your broker uses unusual names.</Banner>}
      <SignalBanner s={s} />
      <KpiStrip s={s} />
      <OilDemo />
      <SpreadChart k={c.stddev_mult} zNow={s.z} />
      <Calculator calc={s.calc} cfg={c} />
      <div className="grid gap-5 xl:grid-cols-2">
        <StrategyConfig cfg={c} onSaved={reload} />
        <GateConfig cfg={c} gate={s.gate} onSaved={reload} />
      </div>
      <OpenTrade s={s} onChange={reload} />
      <div className="grid gap-5 xl:grid-cols-2">
        <OilLive s={s} onChange={reload} />
        <SymbolLock s={s} onChange={reload} />
      </div>
      <OilHistory />
      <Replay />
      <Maintenance s={s} onChange={reload} />
    </div>
  );
}
